import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Order } from '@lib/entities/Order';
import { PayeeDetails } from '@lib/entities/ApplePay';

export interface SubmitOrderPayload {
    captchaToken?: string;
    applePayToken?: string;
    payeeDetails?: PayeeDetails;
    /**
     * Phone the user entered at checkout (account-verification flow). When the user is not
     * phone-validated, this is used for `address_data.phone` instead of the profile phone.
     * Unmasked digits. Ignored when the user is already validated.
     */
    phoneOverride?: string;
    onSuccess?: (order: Order) => void;
    onError?: (error: any) => void;
    /**
     * Settles the caller's promise with the real order outcome (JSAD-7104). Called exactly
     * once, from the saga's `finally`, so it also fires on the guard early-return and on
     * takeLatest cancellation — branches that invoke neither onSuccess nor onError. Apple Pay
     * awaits this to decide the on-device sheet's SUCCESS/FAIL state, so a branch that settles
     * nothing would leave the sheet spinning.
     */
    onSettled?: (placed: boolean) => void;
}

export interface CheckoutState {
    isSubmitting: boolean;
    error?: string;
    order?: Order;
}

const initialState: CheckoutState = {
    isSubmitting: false,
    error: undefined,
    order: undefined
};

const checkoutSlice = createSlice({
    name: 'checkout',
    initialState,
    reducers: {
        submitOrderRequest: (state, action: PayloadAction<SubmitOrderPayload>) => {
            state.isSubmitting = true;
            state.error = undefined;
        },
        submitOrderSuccess: (state, action: PayloadAction<Order>) => {
            state.isSubmitting = false;
            state.order = action.payload;
        },
        submitOrderFailure: (state, action: PayloadAction<string>) => {
            state.isSubmitting = false;
            state.error = action.payload;
        },
        resetCheckout: (state) => {
            state.isSubmitting = false;
            state.error = undefined;
            state.order = undefined;
        }
    }
});

export const { submitOrderRequest, submitOrderSuccess, submitOrderFailure, resetCheckout } =
    checkoutSlice.actions;

export const selectCheckoutIsSubmitting = (state: { checkout: CheckoutState }) =>
    state.checkout.isSubmitting;
export const selectCheckoutError = (state: { checkout: CheckoutState }) => state.checkout.error;
export const selectCheckoutOrder = (state: { checkout: CheckoutState }) => state.checkout.order;

export default checkoutSlice.reducer;
